import { colors } from '@lmc/common/constants'
import PropTypes from 'prop-types'
import React from 'react'
import {
  LabelIcon,
  ListItem,
  ProfilePicture
} from '../../../../components/generic'
import { getIconProps, getLabelText, getSubLabel } from './detailsUtils'

const renderProfilePicture = (props) => {
  const {
    resident: { picture }
  } = props

  return (
    <div style={styles.profilePicture}>
      <ProfilePicture url={picture} />
    </div>
  )
}

// Body render functions

const renderMedications = (props) => {
  const { meds } = props

  if (!meds || !meds.length) return null

  return meds.map((med) => {
    const { taken, PRN } = med

    if (taken && !PRN) return null

    return renderSignedMedication({ ...med })
  })
}

const renderSignedMedication = (props) => {
  const { medicationName, note, PRN, reasonCode } = props

  const labelStyles = PRN ? styles.PRN : styles.reasonCode

  return (
    <div style={styles.signedMed}>
      <div style={labelStyles}>{PRN ? 'PRN' : reasonCode}</div>
      <div style={styles.medDescription}>
        {`${medicationName}. ${note || ''}`}
      </div>
    </div>
  )
}

const renderComment = (props) => {
  const { additionalComment } = props

  if (!additionalComment) return null

  return <div style={styles.comment}>{additionalComment}</div>
}

const renderWitness = (props) => {
  const { witnessedBy } = props

  if (!witnessedBy || !witnessedBy.name) return null

  const { name } = witnessedBy

  return (
    <div style={styles.witness}>
      {`Witnessed by ${name.first} ${name.last}`}
    </div>
  )
}

const renderBody = (props) => {
  const { signed } = props

  if (!signed) return null

  return (
    <div style={styles.bodyContainer}>
      {renderMedications(props)}
      {renderComment(props)}
      {renderWitness(props)}
    </div>
  )
}

// End of body render functions

const renderRight = (props) => {
  const iconProps = getIconProps(props)

  return (
    <LabelIcon
      iconFill={iconProps.fill}
      iconName={iconProps.name}
      iconPosition="right"
      text={getLabelText(props)}
      textFill={colors.darkgrey}
    />
  )
}

/**
@description Renders the resident rounds detail card. Displays
the round medication status for a resident.

@param {object} resident. The resident's info.

@param {object} loggedBy. The log carer's info.

@param {object} witnessedBy. The witness carer's info.

@param {bool} signed. Whether or not the resident's meds
have signed.

@param {string} additionalComment. An optional comment
that's added by a carer for that resident's round.

@param {array} meds. An array of med objects.
med: {
    taken: true / false
    reasonCode: 'text',
    medicationName: 'text',
    note: 'text',
    PRN: true / false,
    logId: { ... },
},

@returns {function} ResidentRoundDetailsCard.
*/

export default function ResidentRoundDetailsCard(props) {
  const { resident } = props

  if (!resident || !resident.name) return null

  return (
    <ListItem
      label={`${resident.name.first} ${resident.name.last}`}
      subLabel={getSubLabel(props)}
      to={`/residents/${resident.id}/emar`}
      renderBody={() => renderBody(props)}
      renderImage={() => renderProfilePicture(props)}
      renderRight={() => renderRight(props)}
    />
  )
}

ResidentRoundDetailsCard.propTypes = {
  additionalComment: PropTypes.string,
  loggedBy: PropTypes.object,
  meds: PropTypes.array,
  resident: PropTypes.object.isRequired,
  signed: PropTypes.bool.isRequired,
  witnessedBy: PropTypes.object
}

const styles = {
  bodyContainer: {
    margin: '0px 0px 0px 60px'
  },
  comment: {
    lineHeight: '22px',
    paddingTop: 8
  },
  medDescription: {
    lineHeight: '22px'
  },
  profilePicture: {
    paddingRight: 6
  },
  PRN: {
    fontWeight: 700,
    fontSize: 12,
    lineHeight: '18px',
    width: 30
  },
  reasonCode: {
    fontWeight: 700,
    lineHeight: '22px',
    width: 30
  },
  signedMed: {
    display: 'flex',
    alignItems: 'baseline',
    paddingTop: 8
  },
  witness: {
    color: colors.darkgrey,
    fontSize: 12,
    lineHeight: '18px',
    paddingTop: 2
  }
}
